import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from "react";
import { apiFetch, getApiBase, onUnauthorized, ApiError } from "../lib/apiClient";
import { cellVisible, LEGACY_OPEN_TABS, type PermissionCell, type PermissionMatrix, type TabId } from "../lib/permissionsCatalog";

// Mirrors the backend UserRole union (artifacts/api-server/src/lib/userStore.ts).
// soc_l1 / soc_l2 are scoped SOC analyst tiers; soc_manager is a SOC team lead
// that sees every operational tab EXCEPT MSSP Overview and has no Dashboard
// Settings access. analyst is a legacy alias kept so existing user rows keep
// working. customer_viewer is the read-only portal.
export type UserRole = "admin" | "developer" | "software_tester" | "soc_manager" | "soc_l1" | "soc_l2" | "analyst" | "customer_viewer";

export type CapabilityMap = Record<string, string[]>;

export interface RoleBranding {
  id: string;
  label: string;
  color?: string;
}

export interface AuthUser {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  allowedCustomerIds: string[];
  /** When role === 'customer_viewer', the single customer this user is scoped to. */
  scopedCustomerId?: string | null;
  /** Per-customer × per-tab × {read,write} matrix. Empty → legacy role behaviour. */
  permissions?: PermissionMatrix;
}

interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  /** Redirects the browser to the Microsoft sign-in page. */
  signIn: () => void;
  signOut: () => Promise<void>;
  /** Re-fetches /api/auth/me. Useful after sign-in completes. */
  refresh: () => Promise<void>;
  /** True if the tab should appear in the nav for this user/customer. */
  canSee: (tab: TabId, customerId: string | undefined) => boolean;
  /** True if the user can read `tab` for the given customer. Mirrors backend logic. */
  canRead: (tab: TabId, customerId: string | undefined) => boolean;
  /** True if the user can perform a write action on `tab` for the given customer. */
  canWrite: (tab: TabId, customerId: string | undefined) => boolean;
  /** Runtime capability check backed by Masters → Permissions Master. */
  canCapability: (moduleId: string, capability: string) => boolean;
  roleBranding: Record<string, RoleBranding>;
  capabilitiesLoading: boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [effectiveCapabilities, setEffectiveCapabilities] = useState<CapabilityMap | null>(null);
  const [roleBranding, setRoleBranding] = useState<Record<string, RoleBranding>>({});
  const [capabilitiesLoading, setCapabilitiesLoading] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const data = await apiFetch<{ user: AuthUser }>("/auth/me");
      setUser(data.user);
    } catch (err) {
      // 401 is the normal "not signed in" state - not an error worth logging.
      if (!(err instanceof ApiError && err.notAuthenticated)) {
        console.warn("auth/me failed", err);
      }
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // First load: figure out who we are.
  useEffect(() => {
    refresh();
  }, [refresh]);

  // Runtime capability matrix used by frontend visibility/buttons. This is a
  // UX gate only; backend routes independently enforce the same capabilities.
  useEffect(() => {
    let cancelled = false;

    if (!user) {
      setEffectiveCapabilities(null);
      setCapabilitiesLoading(false);
      return;
    }

    setCapabilitiesLoading(true);
    apiFetch<{ capabilities?: CapabilityMap }>("/masters/permissions/effective")
      .then((data) => {
        if (!cancelled) setEffectiveCapabilities(data.capabilities ?? {});
      })
      .catch((err) => {
        // Do not brick the UI if this best-effort read fails; backend
        // capability middleware remains authoritative.
        console.warn("capability policy load failed", err);
        if (!cancelled) setEffectiveCapabilities(null);
      })
      .finally(() => {
        if (!cancelled) setCapabilitiesLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [user?.id, user?.role]);

  // Role color/label branding is managed in Masters → Role Master and used by
  // the header/user badge. Best effort only: backend remains authoritative.
  useEffect(() => {
    let cancelled = false;
    if (!user) {
      setRoleBranding({});
      return;
    }
    apiFetch<{ roles?: RoleBranding[] }>("/masters/role-branding")
      .then((data) => {
        if (cancelled) return;
        const map: Record<string, RoleBranding> = {};
        for (const role of data.roles ?? []) map[role.id] = role;
        setRoleBranding(map);
      })
      .catch((err) => {
        console.warn("role branding load failed", err);
        if (!cancelled) setRoleBranding({});
      });
    return () => { cancelled = true; };
  }, [user?.id, user?.role]);

  // If any subsequent API call returns 401, drop the user out of the app
  // (the AuthGate will then render the LoginScreen).
  useEffect(() => {
    return onUnauthorized(() => {
      setUser((prev) => (prev ? null : prev));
    });
  }, []);

  const signIn = useCallback(() => {
    // Pass returnTo so we land back on the same page after the OIDC roundtrip.
    const returnTo = encodeURIComponent(window.location.pathname + window.location.search);
    window.location.href = `${getApiBase()}/auth/login?returnTo=${returnTo}`;
  }, []);

  const signOut = useCallback(async () => {
    try {
      await apiFetch("/auth/logout", { method: "POST" });
    } catch {
      // ignore - even if the server call fails, drop the local user.
    }
    setUser(null);
  }, []);

  // Mirrors `getEffectivePermission` on the server. Keep the two in sync.
  const resolvePerm = useCallback(
    (tab: TabId, customerId: string | undefined): { visible: boolean; read: boolean; write: boolean } => {
      const denied = { visible: false, read: false, write: false };
      if (!user) return denied;
      if (user.role === "admin" || user.role === "developer") {
  return { visible: true, read: true, write: true };
}
      if (user.role === "customer_viewer") {
        // Matrix-authoritative on the scoped customer. With NO matrix entry
        // we fall back to visible+read on every tab (legacy portal default).
        // Portal users are HARD-blocked from writes server-side (every
        // mutating route runs through `blockCustomerViewer`), so we strip
        // `write` here regardless of what the matrix cell says - that way
        // legacy DB rows with stale `write:true` cells don't surface
        // write-only UI (Deep Investigate, Investigate with AI, Close,
        // Close Incident, etc.) that would 403 the moment the portal user clicks.
        if (user.scopedCustomerId !== customerId) return denied;
        const perCust = customerId ? user.permissions?.[customerId] : undefined;
        if (perCust && Object.keys(perCust).length > 0) {
          const cell = perCust[tab];
          if (!cell) {
            // Tabs added after the original 7-tab matrix shipped fall back
            // to the legacy portal default (visible + read) when no
            // explicit cell exists, so a pre-existing override doesn't
            // accidentally hide them. Mirror of the server resolver.
            if (LEGACY_OPEN_TABS.has(tab)) return { visible: true, read: true, write: false };
            return denied;
          }
          const eff = resolveCell(cell);
          return { visible: eff.visible, read: eff.read, write: false };
        }
        return { visible: true, read: true, write: false };
      }
      // SOC staff (soc_l1/l2/manager/analyst): need an allowed customer.
      if (!customerId || !user.allowedCustomerIds.includes(customerId)) {
        return denied;
      }
      const perCust = user.permissions?.[customerId];
      if (perCust && Object.keys(perCust).length > 0) {
        const cell = perCust[tab];
        if (!cell) {
          // See LEGACY_OPEN_TABS comment above - newly-added tabs default
          // to full access when missing from a pre-existing override.
          if (LEGACY_OPEN_TABS.has(tab)) return { visible: true, read: true, write: true };
          return denied;
        }
        return resolveCell(cell);
      }
      // Legacy: no matrix entry → full visible+read+write on every tab.
      return { visible: true, read: true, write: true };
    },
    [user],
  );

  const canSee = useCallback(
    (tab: TabId, customerId: string | undefined) => resolvePerm(tab, customerId).visible,
    [resolvePerm],
  );
  const canRead = useCallback(
    (tab: TabId, customerId: string | undefined) => resolvePerm(tab, customerId).read,
    [resolvePerm],
  );
  const canWrite = useCallback(
    (tab: TabId, customerId: string | undefined) => resolvePerm(tab, customerId).write,
    [resolvePerm],
  );

  const canCapability = useCallback(
    (moduleId: string, capability: string): boolean => {
      if (!user) return false;
      if (user.role === "developer") return true;
      // While the matrix is loading or if the read failed, keep the legacy UI
      // visible. Backend enforcement still blocks disallowed operations.
      if (!effectiveCapabilities) return true;
      return (effectiveCapabilities[moduleId] ?? []).includes(capability);
    },
    [effectiveCapabilities, user],
  );

  return (
    <AuthContext.Provider value={{ user, loading, signIn, signOut, refresh, canSee, canRead, canWrite, canCapability, roleBranding, capabilitiesLoading }}>
      {children}
    </AuthContext.Provider>
  );
}

/**
 * Resolve a stored cell into the effective {visible, read, write} triple.
 * Mirrors `resolveCell` on the server: hidden cells force read=write=false
 * because there's no UI surface for them, and write requires read.
 */
function resolveCell(cell: PermissionCell): { visible: boolean; read: boolean; write: boolean } {
  const visible = cellVisible(cell);
  const read = visible && (cell.read || cell.write);
  const write = read && cell.write;
  return { visible, read, write };
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
